// Types matching Supabase DB schema

export interface ChecklistItem {
  key: string;
  label: string;
  checked: boolean;
}

/** VIB-1633: administrerbar loop på /display/salg. */
export interface DBSalesDisplaySlide {
  id: string;
  kind: "message" | "image" | "video" | "widget";
  title: string;
  body: string | null;
  media_path: string | null;
  widget_key: "kpi" | "leaderboard" | "pipeline" | "hall_of_fame" | null;
  duration_seconds: number;
  sort_order: number;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface DBProject {
  id: string;
  project_code: string | null;
  name: string;
  customer_id: string | null;
  total_amount: number | null;
  bf_estimated: number | null;
  bf_actual: number | null;
  // VIB-1120: provisjon låst ved prosjektlukking. Når commission_locked_at er
  // satt, brukes commission_rate_pct som frossen sats i stedet for live config.
  commission_rate_pct: number | null;
  commission_locked_at: string | null;
  startup_invoice_pct: number | null;
  /**
   * VIB-686: Fleksible faktureringsvilkår. Source of truth for milepæl-
   * fakturering. Format: [{ label: "Oppstart", pct: 60 }, { label: "Levering",
   * pct: 40 }]. Sum av pct skal være 100. Default 60/40 fra DB.
   *
   * Backward compat: `startup_invoice_pct` = `invoice_terms[0].pct` lagres
   * også for filer som ennå ikke leser invoice_terms.
   */
  invoice_terms: Array<{ label: string; pct: number }>;
  estimated_deadline: string | null;
  /** VIB-1297: valgfri forventet oppstartsdato. Kun visning i dag (kommende-status) — ingen KPI/pipeline-beregning leser feltet ennå. */
  estimated_start_date: string | null;
  accounting_completed_date: string | null;
  // Prosjektfase (én aktiv fase)
  project_phase: string | null;

  // Lukkingssjekkliste
  closing_checklist: ChecklistItem[];

  // Legacy fasestatuser (beholdt for bakoverkompatibilitet)
  phase_preprod: string | null;
  phase_production: string | null;
  phase_postprod: string | null;

  // Møte-statuser
  meeting_startup_status: string | null;
  meeting_startup_reason: string | null;
  meeting_startup_checklist: ChecklistItem[];
  debrief_status: string | null;
  debrief_reason: string | null;
  debrief_checklist: ChecklistItem[];

  // Møtenotater
  meeting_startup_notes: string | null;
  debrief_notes: string | null;

  // Andre statuser
  extras_status: string | null;
  additions_status: string | null;
  travel_cost_status: string | null;
  clean422_status: string | null;
  commission_status: string | null;
  accounting_complete: string | null;

  // Booleans
  hours_pp_estimated: boolean;
  hours_pp_distributed: boolean;

  // Linker
  offer_pdf_url: string | null;
  harddisk: string | null;

  // Ekstern-IDer
  poweroffice_project_id: string | null;
  poweroffice_customer_id: string | null;

  // Personreferanser (UUID)
  production_lead_id: string | null;
  advisor_id: string | null;

  // Referansefelter (VIB-163)
  deres_ref: string | null;
  deres_ref_kode: string | null;
  var_ref: string | null;
  overhead: number | null;

  // Fakturainformasjon (VIB-184, VIB-218)
  payment_due_days: number | null;
  invoice_reference: string | null;
  invoice_email: string | null;
  customer_reference_person: string | null;
  customer_reference_number: string | null;
  our_reference_person: string | null;
  our_project_code: string | null;

  // Flagg
  is_internal: boolean;
  is_active: boolean;
  /** VIB-666: 'fixed' = fastpris/tilbud, 'hourly' = time-jobb (T&M). */
  project_type: "fixed" | "hourly";
  /** VIB-909: PL-styrt flagg — reise faktureres per medgått tid. Default false. */
  bills_travel_per_medgatt?: boolean;

  // Økonomi
  additions: number | null;
  travel: number | null;
  commission: number | null;
  differanse_sum: number | null;
  differanse_pct: number | null;
  differanse_pr_pers: number | null;
  est_pct_bf: number | null;
  pct_bf: number | null;
  invoice_status: string | null;

  // PO-synkede KPIer
  bf_diff_sum: number | null;
  bf_diff_pct: number | null;
  bf_diff_per_person: number | null;
  bf_est_pct: number | null;
  bf_pct: number | null;
  project_status: string | null;

  // Timestamps
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  completed_at: string | null;
}

// VIB-783: Møterader per prosjekt. Erstatter meeting_startup_* / debrief_*-
// kolonnene på DBProject. Statusmøter kan finnes mange ganger per prosjekt;
// oppstart og debrief én gang hver (håndhevet via partial unique index).
export type MeetingType = "startup" | "status" | "debrief";

export interface DBProjectMeeting {
  id: string;
  project_id: string;
  meeting_type: MeetingType;
  status: string | null;
  reason: string | null;
  checklist: ChecklistItem[];
  notes: string | null;
  held_at: string | null;
  what_went_well: string | null;
  what_went_poorly: string | null;
  created_at: string;
  updated_at: string;
}

// VIB-784: System-wide checklist defaults per møtetype. Snapshotes til
// project_meetings.checklist ved opprettelse — endringer her påvirker IKKE
// eksisterende møter.
export interface ChecklistTemplateItem {
  key: string;
  label: string;
}

export interface DBChecklistTemplate {
  id: string;
  meeting_type: MeetingType;
  items: ChecklistTemplateItem[];
  updated_at: string;
  updated_by: string | null;
}

export interface DBCustomer {
  id: string;
  name: string;
  organization_number: string | null;
  contact_person: string | null;
  contact_email: string | null;
  /**
   * VIB-2521: PowerOffice `InvoiceEmailAddress`. Dette er adressen fakturaen
   * faktisk sendes til når `projects.invoice_email` ikke overstyrer per
   * faktura — ikke `contact_email`, som bare er generell kontaktadresse.
   */
  invoice_email_address: string | null;
  invoice_email_cc: string | null;
  contact_phone: string | null;
  poweroffice_customer_id: number | null;
  /** ISO 3166-1 alpha-2. Default NO. */
  country_code: string;
  /** CRM-1.4: lokal statusoverstyring (ikke PO-felt). Hentes av useCustomers. */
  status_override?: "warm" | "medium" | "cold" | null;
  status_override_reason?: string | null;
  status_override_until?: string | null;
  status_override_by?: string | null;
  status_override_at?: string | null;
}

/** CRM-1.4: rad fra viewet customer_status_v. */
export interface DBCustomerStatusRow {
  customer_id: string;
  derived_status: "warm" | "medium" | "cold";
  effective_status: "warm" | "medium" | "cold";
  is_overridden: boolean;
  override_reason: string | null;
  override_until: string | null;
  override_by_name: string | null;
  last_purchase_at: string | null;
  cools_at: string | null;
  has_offers: boolean;
}

export type CustomerContactSource = "manual" | "po_mirror" | "brreg_role" | "linkedin" | "agent";

/** CRM-1.1: kontaktperson på kunde. phone er E.164. Slettes ved anonymisering. */
export interface DBCustomerContact {
  id: string;
  /** NULL for kontakter som tilhører et lead (CRM-4.0); da er lead_id satt. */
  customer_id: string | null;
  lead_id: string | null;
  full_name: string;
  title: string | null;
  email: string | null;
  phone: string | null;
  is_primary: boolean;
  source: CustomerContactSource;
  suggested_by_agent: boolean;
  confirmed_at: string | null;
  confirmed_by: string | null;
  is_active: boolean;
  inactive_reason: string | null;
  anonymized_at: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/** CRM-1.2: kundeeier (fan-out fra salgsenhet, som project_owners). */
export interface DBCustomerOwner {
  id: string;
  customer_id: string;
  user_id: string;
  sales_unit_id: string | null;
  seeded_from_project_id: string | null;
  created_by: string | null;
  created_at: string;
}

export type CustomerActivityType =
  | "email_out" | "email_in" | "call" | "sms_out" | "meeting" | "note" | "summary";
export type CustomerActivityStatus = "logged" | "scheduled" | "sent" | "received" | "failed" | "cancelled";

/** CRM-1.3: kontaktlogg. email_*, sms_out skrives bare av edge functions. */
/** VIB-2979: Grunnlag i tilbudsarbeidsrommet, 1:1 med offers. */
export interface DBOfferWorkspace {
  offer_id: string;
  target_audience: string | null;
  budget_frame_nok: number | null;
  ai_sources: unknown;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

export type OfferIdeaCardKind = "note" | "image" | "link" | "film" | "question" | "excerpt";

export interface DBOfferIdeaCard {
  id: string;
  offer_id: string;
  kind: OfferIdeaCardKind;
  body: string;
  url: string | null;
  storage_path: string | null;
  vimeo_id: string | null;
  source_type: string | null;
  source_id: string | null;
  source_label: string | null;
  source_date: string | null;
  x: number;
  y: number;
  width: number;
  group_id: string | null;
  use_for_ai: boolean;
  deleted_at: string | null;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface DBOfferIdeaGroup {
  id: string;
  offer_id: string;
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
  deleted_at: string | null;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface DBCustomerActivity {
  id: string;
  customer_id: string;
  opportunity_id: string | null;
  contact_id: string | null;
  type: CustomerActivityType;
  direction: "in" | "out" | null;
  subject: string | null;
  body: string | null;
  body_html: string | null;
  occurred_at: string;
  user_id: string | null;
  gmail_thread_id: string | null;
  gmail_message_id: string | null;
  rfc_message_id: string | null;
  status: CustomerActivityStatus;
  send_at: string | null;
  sent_at: string | null;
  error: string | null;
  metadata: Record<string, unknown>;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/** CRM-1.3: planlagt utgående e-post/SMS. */
export interface DBCrmOutbox {
  id: string;
  kind: "email" | "sms";
  activity_id: string | null;
  meeting_id: string | null;
  reminder_kind: string | null;
  recipient: string;
  sender_user_id: string | null;
  payload: Record<string, unknown>;
  send_at: string;
  status: "pending" | "sending" | "sent" | "failed" | "cancelled";
  attempts: number;
  last_error: string | null;
  sent_at: string | null;
  provider_ref: string | null;
  idempotency_key: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export type OpportunityStage = "outreach" | "meeting" | "offer" | "won" | "lost";
export type OpportunitySource = "signal" | "lead" | "manual";

/** CRM-2.1: prosess i pipen. offer/won/lost styres av tilbudet (DB-trigger). */
export interface DBOpportunity {
  id: string;
  customer_id: string | null;
  lead_id: string | null;
  title: string;
  theme: string | null;
  owner_id: string;
  source: OpportunitySource;
  signal_id: string | null;
  stage: OpportunityStage;
  stage_entered_at: string;
  offer_id: string | null;
  project_id: string | null;
  lost_reason: string | null;
  lost_at: string | null;
  won_at: string | null;
  last_activity_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export type FollowUpPriority = "asap" | "this_week" | "nice";
export type FollowUpChannel = "email" | "phone" | "meeting" | "other";
export type FollowUpSource = "manual" | "rule" | "meeting_summary" | "signal";
export type FollowUpStatus = "open" | "done" | "cancelled";
export type FollowUpOutcome = "email_sent" | "phone_done" | "closed";

/** CRM-2.2: oppfølging. Lukkes bare via close_follow_up, utsettes via postpone_follow_up. */
export interface DBFollowUp {
  id: string;
  customer_id: string;
  opportunity_id: string | null;
  owner_id: string | null;
  contact_id: string | null;
  title: string;
  description: string | null;
  due_date: string;
  priority: FollowUpPriority;
  channel: FollowUpChannel | null;
  source: FollowUpSource;
  rule_key: string | null;
  source_activity_id: string | null;
  draft_subject: string | null;
  draft_body: string | null;
  draft_generated_at: string | null;
  draft_model: string | null;
  status: FollowUpStatus;
  outcome: FollowUpOutcome | null;
  outcome_note: string | null;
  outcome_activity_id: string | null;
  postponed_count: number;
  closed_at: string | null;
  closed_by: string | null;
  next_follow_up_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/** CRM-1.5: Gmail-synk per selger. Skrives kun av edge functions. */
export interface DBCrmGmailSyncState {
  user_id: string;
  email: string;
  history_id: string | null;
  last_synced_at: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
}

export type SignalStrength = "high" | "medium" | "low";
export type SignalStatus = "open" | "claimed" | "archived" | "expired";
export type SignalFrequency = "weekly" | "daily";
export type MonitoringSourceKind = "website" | "instagram" | "facebook" | "linkedin";
export type SignalAgentSource = MonitoringSourceKind | "brreg" | "news";
export type SignalAgentScope = "monitored" | "customers" | "segment";

/** En rolle fra Brønnøysund (/roller), lagret i customer_company_info.roles. */
export interface CompanyRole {
  role_code: string;
  role_label: string;
  person_name: string;
  since: string | null;
}

/** CRM-3.0: bedriftsinfo fra Brønnøysund. Rå svar (raw_*) hentes ikke av UI. */
export interface DBCustomerCompanyInfo {
  customer_id: string;
  organization_number: string;
  legal_name: string | null;
  organization_form: string | null;
  industry_code: string | null;
  industry_description: string | null;
  employees: number | null;
  founded_on: string | null;
  website: string | null;
  municipality: string | null;
  business_address: Record<string, unknown> | null;
  is_bankrupt: boolean | null;
  is_winding_up: boolean | null;
  roles: CompanyRole[] | null;
  fiscal_year: number | null;
  revenue_nok: number | null;
  operating_result_nok: number | null;
  net_result_nok: number | null;
  equity_nok: number | null;
  previous_fiscal_year: number | null;
  previous_revenue_nok: number | null;
  fetched_at: string;
  last_error: string | null;
}

/** CRM-3.0: overvåkning per kilde. override=true: rådgiver har satt is_active manuelt. */
export interface DBCustomerMonitoringSource {
  id: string;
  customer_id: string;
  source: MonitoringSourceKind;
  identifier: string | null;
  identifier_source: "manual" | "brreg" | "agent" | null;
  is_active: boolean;
  override: boolean;
  frequency: SignalFrequency;
  next_run_at: string | null;
  last_run_at: string | null;
  last_error: string | null;
  cost_total_nok: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/** CRM-3.0: signalagent. Tellere og kostnad skrives bare av edge/RPC. */
export interface DBSignalAgent {
  id: string;
  name: string;
  description: string | null;
  owner_id: string | null;
  is_system: boolean;
  scope_type: SignalAgentScope;
  customer_ids: string[] | null;
  segment: LeadSegment | null;
  sources: SignalAgentSource[];
  instruction: string;
  signal_type: string;
  contact_reason: string;
  default_strength: SignalStrength;
  frequency: SignalFrequency;
  is_active: boolean;
  cost_cap_nok_month: number | null;
  cost_month_nok: number;
  cost_month: string | null;
  dismiss_count: number;
  claim_count: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/** CRM-3.0: salgssignal. Skrives bare av edge functions og RPC-er. */
export interface DBCustomerSignal {
  id: string;
  customer_id: string;
  agent_id: string | null;
  run_id: string | null;
  signal_type: string;
  source: string;
  evidence_url: string | null;
  evidence_quote: string | null;
  headline: string;
  why_now: string;
  first_step: string;
  draft_subject: string | null;
  draft_body: string | null;
  strength: SignalStrength;
  status: SignalStatus;
  claimed_by: string | null;
  claimed_at: string | null;
  opportunity_id: string | null;
  expires_at: string;
  dedupe_key: string | null;
  created_at: string;
  updated_at: string;
}

/** CRM-3.5: ett kort i signalkøen (RPC next_signals_for_me). */
export interface DBSignalQueueItem {
  id: string;
  customer_id: string;
  customer_name: string;
  agent_id: string | null;
  agent_name: string | null;
  signal_type: string;
  source: string;
  evidence_url: string | null;
  evidence_quote: string | null;
  headline: string;
  why_now: string;
  first_step: string;
  draft_subject: string | null;
  draft_body: string | null;
  strength: SignalStrength;
  created_at: string;
  expires_at: string;
  effective_status: "warm" | "medium" | "cold" | null;
  total_value: number | null;
  primary_contact_name: string | null;
  last_contact_at: string | null;
  last_contact_type: string | null;
  last_contact_user_name: string | null;
  open_opportunities: { id: string; title: string; stage: string; owner_name: string | null }[];
  is_owner: boolean;
  recently_contacted: boolean;
  customer_logo_url: string | null;
  primary_contact_id: string | null;
  primary_contact_title: string | null;
  primary_contact_email: string | null;
  primary_contact_phone: string | null;
  /** Kundens e-post og telefon fra PowerOffice, når hovedkontakten mangler dem. */
  customer_email: string | null;
  customer_phone: string | null;
  /** Antall signaler i køen totalt (for «1 / N»), ikke bare de som er hentet. */
  queue_total: number;
}

export interface DBUser {
  id: string;
  auth_id: string | null;
  full_name: string;
  email: string;
  role: string;
  specialization: string | null;
  hourly_cost_rate: number | null;
  hourly_sales_rate: number | null;
  monthly_capacity_hours: number | null;
  target_billing_rate: number | null;
  is_active: boolean;
  phone: string | null;
  avatar_url: string | null;
  birthday: string | null;
  hire_date: string | null;
  job_title: string | null;
  /** CRM-4.0: kan bookes som vert for kundemøte (krever Sølvrev-konto i Google Workspace). */
  can_host_meetings: boolean;
  /** VIB-2621: id på nyeste endringslogg-oppføring brukeren har sett popup for. */
  changelog_seen_id: string | null;
  /** VIB-2917: avdeling (Oslo/Bergen). */
  department_id: string | null;
  /** VIB-2917: utstyrsansvarlig, kan registrere og endre utstyr. */
  can_manage_equipment: boolean;
  created_at: string;
  updated_at: string;
}

/** VIB-2917: Sølvrevs avdelinger. */
export interface DBDepartment {
  id: string;
  name: string;
  sort_order: number;
}

/** VIB-2917: utstyrskategori. bookable=false betyr registrert for forsikring, ikke booking. */
export interface DBEquipmentCategory {
  id: string;
  name: string;
  sort_order: number;
  bookable: boolean;
}

/**
 * VIB-2917: utstyr slik interne ser det (viewet equipment_items_public).
 * Admin og utstyrsansvarlige leser basistabellen og får i tillegg beløpene.
 */
export interface DBEquipmentItem {
  id: string;
  name: string;
  category_id: string;
  department_id: string | null;
  quantity: number;
  serial_numbers: string[];
  status: "ok" | "in_service" | "missing" | "sold" | "retired";
  bookable: boolean | null;
  insured_until: string | null;
  insurance_declined: boolean;
  purchase_date: string | null;
  retired_date: string | null;
  rental_price_day: number | null;
  parts: string[];
  comment: string | null;
  source: string | null;
  legacy_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  insurance_value?: number | null;
  purchase_price?: number | null;
  extra_price?: number | null;
  /** VIB-2918: modellen enheten er av, eller hvorfor den ikke får produktdata. */
  product_id: string | null;
  product_skip_reason: string | null;
}

/** VIB-2918: utstyrsmodell (merke + modell). Produktdata deles av alle enheter av modellen. */
export interface DBEquipmentProduct {
  id: string;
  brand_id: string | null;
  brand_name: string;
  model: string;
  category_id: string | null;
  description: string | null;
  manufacturer_part_no: string | null;
  specs: { label: string; value: string }[];
  product_url: string | null;
  enriched_at: string | null;
  created_at: string;
}

/** VIB-2918: bilde eller dokument i bøtta equipment-media, for en modell eller en enhet. */
export interface DBEquipmentMedia {
  id: string;
  product_id: string | null;
  item_id: string | null;
  kind: "image" | "manual" | "datasheet" | "document";
  storage_path: string;
  title: string | null;
  source_url: string | null;
  source: "upload" | "manufacturer" | "web";
  mime_type: string | null;
  size_bytes: number | null;
  created_at: string;
}

/** VIB-2919: utstyrsbooking (prosjekt eller intern med tittel). */
export interface DBEquipmentBooking {
  id: string;
  project_id: string | null;
  title: string | null;
  responsible_user_id: string;
  status: "active" | "cancelled";
  created_by: string;
}

/** VIB-2919: aktiv bookinglinje fra viewet equipment_booking_lines. date_to er inklusiv. */
export interface DBEquipmentBookingLine {
  id: string;
  booking_id: string;
  item_id: string;
  quantity: number;
  date_from: string;
  date_to: string;
  note: string | null;
  project_id: string | null;
  title: string | null;
  responsible_user_id: string;
  created_by: string;
  project_code: string | null;
  project_name: string | null;
  responsible_name: string | null;
  item_name: string;
  item_quantity: number;
  item_status: DBEquipmentItem["status"];
  item_department_id: string | null;
  item_department_name: string | null;
}

/** VIB-2919: dobbelbooking fra viewet equipment_booking_conflicts (én rad per linjepar). */
export interface DBEquipmentBookingConflict {
  booking_item_id: string;
  booking_id: string;
  item_id: string;
  item_name: string;
  other_booking_item_id: string;
  other_booking_id: string;
  other_project_id: string | null;
  other_project_code: string | null;
  other_project_name: string | null;
  other_title: string | null;
  other_quantity: number;
  other_date_from: string;
  other_date_to: string;
  other_responsible_name: string | null;
}

/** VIB-2919: «Brukt 12 mnd» fra viewet equipment_usage_12m. */
export interface DBEquipmentUsage {
  item_id: string;
  booked_days: number;
  bookings: number;
}

/** VIB-2703: mappe i dokumentarkivet under /hjelp. */
export interface DBDocumentFolder {
  id: string;
  name: string;
  slug: string;
  read_min_role: string;
  write_min_role: string;
  /**
   * VIB-2721: undermappe-forelder. NULL = mappa ligger på rot.
   *
   * Tilgangen er OG over hele kjeden (`can_read_document_folder`), så en mappe
   * du ser har alltid en forelder du også ser. Treet bygges av
   * `buildFolderTree` i `src/lib/document-tree.ts`.
   */
  parent_id: string | null;
  /** De fire forhåndsopprettede mappene kan ikke slettes eller bytte slug. */
  is_system: boolean;
  sort_order: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

/**
 * VIB-2703: innhold i en mappe. `kind` skiller en opplastet fil fra et
 * dokument skrevet i systemet — DB-en har en CHECK som holder de to formene
 * fra hverandre, så feltene under er aldri satt samtidig.
 */
export interface DBDocument {
  id: string;
  folder_id: string;
  title: string;
  kind: "file" | "page";
  file_path: string | null;
  file_size: number | null;
  mime_type: string | null;
  content_html: string | null;
  /**
   * VIB-2722: ansvarlig for dokumentet. Ikke det samme som `created_by`, som
   * sier hvem som lagde raden og aldri endres.
   */
  owner_id: string | null;
  /**
   * VIB-2725: øker med én hver gang tittel, innhold eller fil endres. Settes av
   * DB-triggeren `bump_document_version`, aldri av klienten.
   */
  version: number;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface DBInvoice {
  id: string;
  project_id: string | null;
  invoice_type: string | null;
  /** BUG-M2: 0-basert milepæl-posisjon i invoice_terms[1..n-1]. NULL = ikke milepæl (mersalg/oppstart/slutt). */
  milestone_index: number | null;
  amount: number;
  amount_excl_vat: number | null;
  status: string | null;
  description: string | null;
  poweroffice_invoice_id: string | null;
  poweroffice_status: string | null;
  invoice_no: number | null;
  pdf_url: string | null;
  invoice_date: string | null;
  due_date: string | null;
  paid_date: string | null;
  created_at: string;
  updated_at: string;
  /** PO SalesOrder GUID — beholdes etter sending for SentState-poll og PDF. */
  po_sales_order_id: string | null;
  /** Merking-felter som speiler PO sin SalesOrderCompletePostDto. */
  customer_reference: string | null;
  purchase_order_reference: string | null;
  contract_no: string | null;
  /** Leveringsmetode brukt ved sending ("Auto" / "EHF" / "PdfByEmail" / ...). */
  delivery_type: string | null;
  delivery_email: string | null;
  /** True hvis fakturaen ble sendt direkte fra Sølvrev (vs manuelt fra Go). */
  sent_via_api: boolean | null;
  /** Siste feilmelding fra PO ved send-forsøk. Tøm når sendt OK. */
  last_send_error: string | null;
  /** For kreditnota: FK til original-fakturaen som krediteres. */
  related_invoice_id: string | null;
  /** VIB-792: manuelt registrert inntektsrad (ingen PO-faktura). Kun superadmin. */
  is_manual: boolean | null;
  /** VIB-1253: PO CustomerNo fra OutgoingInvoice-DTO (IKKE poweroffice_customer_id — se sync-kommentar i sync-poweroffice-invoices). */
  po_customer_no: number | null;
  /** PowerOffice MatchId: poster med samme verdi er avstemt mot hverandre. */
  po_match_id: number | null;
  /** VIB-3030: kunden fakturaen gikk til. NULL = prosjektets kunde. */
  customer_id: string | null;
  /** VIB-1253: kundenavn hentet fra PO /Customers ved sync, fallback når fakturaen ikke er koblet til et prosjekt med kunde. */
  customer_name: string | null;
  /** VIB-1291: FK til recurring_agreements — satt for fakturaer generert av fakturamotoren (VIB-1292). */
  recurring_agreement_id: string | null;
  /** VIB-994: hvilket T&M-mersalg denne fakturaen tilhører (et T&M-mersalg kan ha flere fakturaer — løpende regning). Valgfri: kun T&M-fakturaer setter denne. */
  upsell_id?: string | null;
}

export type RecurringAgreementType = "recurring" | "rental" | "storage";
export type RecurringAgreementInterval = "monthly" | "quarterly" | "yearly";
export type RecurringAgreementStatus = "active" | "paused" | "ended";

/** Linje-shape delt med create-invoice sine request-linjer (VIB-1291). */
export interface RecurringAgreementLine {
  description: string;
  quantity: number;
  unit_price: number;
  product_code?: string;
  line_type?: "Normal" | "Text";
}

/**
 * VIB-1291: løpende avtaler (recurring/rental/storage). Fakturamotoren
 * (VIB-1292) leser status/next_run_date/deleted_at direkte fra denne
 * tabellen. Storage-kolonnene (included_until, storage_gb, storage_location)
 * brukes først av VIB-1308.
 */
export interface DBRecurringAgreement {
  id: string;
  customer_id: string;
  project_id: string | null;
  type: RecurringAgreementType;
  description: string | null;
  rental_object: string | null;
  lines: RecurringAgreementLine[];
  billing_interval: RecurringAgreementInterval;
  start_date: string;
  end_date: string | null;
  next_run_date: string;
  included_until: string | null;
  storage_gb: number | null;
  storage_location: string | null;
  auto_send: boolean;
  status: RecurringAgreementStatus;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

/** VIB-1308: konfigurerbart trinnpris-nivå for lagringsfakturering. */
export interface DBStoragePricingTier {
  id: string;
  min_gb: number;
  /** NULL = øverste trinn uten tak. */
  max_gb: number | null;
  price: number;
  price_model: "flat" | "per_gb";
  valid_from: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface DBVoucher {
  id: string;
  po_voucher_id: string;
  po_invoice_id: string | null;
  voucher_no: number | null;
  voucher_type: string;
  supplier_name: string | null;
  /** VIB-2926: PowerOffice-leverandørens Id. Null for bilag uten oppslag (f.eks. bildelesing). */
  po_supplier_id: number | null;
  description: string | null;
  amount: number | null;
  currency: string;
  voucher_date: string | null;
  due_date: string | null;
  project_code: string | null;
  project_id: string | null;
  /** Status-livssyklus, se `20260506160000_voucher_approval_workflow.sql`:
   *  - pending_approval: hentet fra PO sin /VoucherApproval-kø, venter på PL
   *  - forwarded_to_dl:  PL har godkjent, ligger hos daglig leder i Go
   *  - approved:         bokført i PO (kommer inn via /AccountTransactions-sync)
   *  - rejected:         PL har avvist
   *  - pending:          legacy, ikke i bruk for nye rader */
  status: "pending" | "pending_approval" | "forwarded_to_dl" | "approved" | "rejected";
  /** Direkte FK til budgetlinjen bilaget er knyttet til. Erstatter
   *  voucher_approvals.budget_line_id (som var en feil bruk — kobling er
   *  ikke en approval-handling). */
  budget_line_id: string | null;
  /** Når bilaget dukket opp i PO sin /VoucherApproval-kø. Null så snart
   *  PL har tatt stilling (godkjent/avvist). */
  pending_approval_since: string | null;
  /** Tidspunkt PL trykket godkjenn (= bilaget ble videresendt til DL). */
  forwarded_to_dl_at: string | null;
  /** PL som godkjente bilaget. */
  forwarded_by_user_id: string | null;
  /** Kommentaren som ble sendt til PO ved PL-beslutning. Vises i
   *  Sølvrev-UI så DL/PL kan se hva som ble sagt. */
  po_approval_comment: string | null;
  has_pdf: boolean;
  has_images: boolean;
  has_ehf: boolean;
  /** VIB-1046: Storage-sti til side-bildet fanget mens bilaget var aktivt i
   *  PO sin /VoucherApproval-kø (feature_voucher_image_capture). NULL =
   *  ingen fanget kopi. Slettes/nulles av sync-poweroffice-vouchers når
   *  bilaget bokføres. */
  captured_image_path: string | null;
  /** VIB-1197: PO VoucherId for originalen denne reverseringen opphever. Satt
   *  KUN på ReversalVouchers (negative bilag). Tilstedeværelse → bilaget er en
   *  reversering og er låst for fordeling. */
  po_reversed_voucher_id: string | null;
  /** VIB-1197: PO VoucherId for originalen dette korrigerte bilaget erstatter.
   *  Satt kun på korrigerings-bilag. */
  po_correction_of_voucher_id: string | null;
  created_at: string;
  updated_at: string;
  /** Beholdes for ekte approval-historikk når VoucherApproval-flyten kommer. */
  voucher_approvals?: { budget_line_id: string | null; decision: string }[];
}

// VIB-997: Fordeling av bokført bilag på én eller flere budsjettlinjer
export interface VoucherAllocation {
  id: string;
  voucher_id: string;
  budget_line_id: string | null;
  amount: number;
  description: string | null;
  po_account_id: number | null;
  source: "po" | "manual" | "vision";
  created_at: string;
}

// VIB-997: Mapping fra PO-kontonummer til budsjettlinjemaler
export interface VoucherAccountMapping {
  po_account_id: number;
  template_id: string;
  updated_at: string;
}

// VIB-402: Aktiv timer per bruker (én rad per user_id)
export interface DBActiveTimer {
  user_id: string;
  project_id: string;
  project_name: string;
  task_id: string | null;
  task_description: string | null;
  phase: string | null;
  budget_line_id: string | null;
  assignment_id: string | null;
  started_at: string;
  updated_at: string;
}

export interface DBVoucherApproval {
  id: string;
  voucher_id: string;
  approved_by: string;
  decision: string;
  budget_line_id: string | null;
  comment: string | null;
  decided_at: string;
}

// Fase-status fargekart
export const PHASE_STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  "Ikke aktuelt": { bg: "bg-gray-100", text: "text-gray-500" },
  "Ikke påbegynt": { bg: "bg-gray-100", text: "text-gray-500" },
  "Skal avholdes": { bg: "bg-orange-100", text: "text-orange-700" },
  "Under arbeid": { bg: "bg-blue-100", text: "text-blue-700" },
  "Ferdig, og avtalt inn...": { bg: "bg-emerald-100", text: "text-emerald-700" },
  "Ferdig": { bg: "bg-emerald-100", text: "text-emerald-700" },
  "Ferdig, og avtalt inntekt samsvarer med tilbud": { bg: "bg-emerald-100", text: "text-emerald-700" },
  "Utført": { bg: "bg-emerald-100", text: "text-emerald-700" },
  "Påbegynt postprod": { bg: "bg-purple-100", text: "text-purple-700" },
  "Jobber med revid...": { bg: "bg-yellow-100", text: "text-yellow-700" },
  "Utkast sendt": { bg: "bg-yellow-100", text: "text-yellow-700" },
  "Godkjent": { bg: "bg-green-100", text: "text-green-700" },
  "Ikke fakturert": { bg: "bg-gray-100", text: "text-gray-500" },
  "Delvis fakturert": { bg: "bg-yellow-100", text: "text-yellow-700" },
  "Fakturert": { bg: "bg-green-100", text: "text-green-700" },
  "Løpende faktura...": { bg: "bg-blue-100", text: "text-blue-700" },
  "Løpende fakturering": { bg: "bg-blue-100", text: "text-blue-700" },
  "Skal ikke faktu...": { bg: "bg-gray-100", text: "text-gray-400" },
  "Skal ikke faktureres": { bg: "bg-gray-100", text: "text-gray-400" },
  "Intern prosjekt...": { bg: "bg-gray-100", text: "text-gray-400" },
  "Intern prosjektering": { bg: "bg-gray-100", text: "text-gray-400" },
  "Ikke besvart": { bg: "bg-gray-100", text: "text-gray-500" },
  "Ingen statister": { bg: "bg-gray-100", text: "text-gray-400" },
  "Ikke klart": { bg: "bg-gray-100", text: "text-gray-500" },
};

// Fallback for ukjente statuser
export function getPhaseColor(status: string | null): { bg: string; text: string } {
  if (!status) return { bg: "bg-gray-50", text: "text-gray-400" };
  return PHASE_STATUS_COLORS[status] ?? { bg: "bg-gray-100", text: "text-gray-600" };
}

// ---------------------------------------------------------------------------
// CRM fase 4: leads og møter
// ---------------------------------------------------------------------------

export type LeadListSource = "csv" | "brreg_search" | "agent" | "manual" | "sheet";

/** Segmentdefinisjon for Brønnøysund-søk og segmentagenter (lead_lists.segment, signal_agents.segment). */
export interface LeadSegment {
  naeringskode?: string[];
  kommunenummer?: string[];
  fylke?: string[];
  employees_min?: number | null;
  employees_max?: number | null;
  revenue_min_nok?: number | null;
  revenue_max_nok?: number | null;
  /** Agentens instruks (CRM-4.2). */
  instruction?: string;
}

/** CRM-4.0: kuratert liste i leads-poolen. */
export interface DBLeadList {
  id: string;
  name: string;
  source: LeadListSource;
  segment: LeadSegment | null;
  agent_id: string | null;
  curated_by: string | null;
  priority: number;
  is_active: boolean;
  notes: string | null;
  /** VIB-3032: bookeren som eier listen. null = felles liste. */
  owner_id: string | null;
  /** VIB-3032: ringemanus som vises på leadkortet. */
  script: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export type LeadStatus = "open" | "locked" | "in_pipe" | "customer" | "parked" | "irrelevant";
export type LeadOutcome =
  | "no_answer" | "not_relevant" | "call_back" | "meeting_booked" | "email_sent" | "wrong_number"
  // VIB-3050: «Skal sende mail» på Leads-tavlen.
  | "email_planned";
export type LeadPriority = "A" | "B" | "C";
export type LeadRejectionReason =
  | "has_supplier" | "has_video" | "not_now" | "no_need" | "group_decides" | "not_target" | "other";

/** CRM-4.0: kaldt lead. Skrives bare av RPC-er og edge functions. */
export interface DBLead {
  id: string;
  list_id: string;
  organization_number: string | null;
  name: string;
  industry_code: string | null;
  industry_description: string | null;
  employees: number | null;
  revenue_nok: number | null;
  website: string | null;
  municipality: string | null;
  address: Record<string, unknown> | null;
  brreg_raw: Record<string, unknown> | null;
  evidence_url: string | null;
  evidence_quote: string | null;
  /** VIB-3032: A ringes først, så B og C. */
  priority: LeadPriority | null;
  /** VIB-3032: hvorfor selskapet er relevant (åpningsvinkelen). */
  pitch: string | null;
  notes: string | null;
  phone_status: "direct" | "uncertain" | null;
  segment: string | null;
  /** VIB-3032: hvorfor leadet ble avvist. */
  rejection_reason: LeadRejectionReason | null;
  /** VIB-3032: når et avvist lead vurderes på nytt. null = avsluttet for godt. */
  revisit_on: string | null;
  status: LeadStatus;
  locked_by: string | null;
  locked_until: string | null;
  preferred_user_id: string | null;
  /** VIB-3050: når leadet kom inn i køen til preferred_user_id. */
  assigned_at: string | null;
  next_eligible_at: string | null;
  parked_until: string | null;
  attempts_count: number;
  last_attempt_at: string | null;
  last_outcome: LeadOutcome | null;
  converted_customer_id: string | null;
  opportunity_id: string | null;
  anonymized_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/** CRM-4.0: ett ringeforsøk. */
export interface DBLeadAttempt {
  id: string;
  lead_id: string;
  user_id: string;
  outcome: LeadOutcome;
  note: string | null;
  call_back_at: string | null;
  meeting_id: string | null;
  created_at: string;
}

export type MeetingLocationType = "meet" | "physical";
export type MeetingStatus = "scheduled" | "held" | "cancelled" | "no_show" | "unknown";
export type TranscriptionStatus =
  | "not_applicable" | "pending" | "config_failed" | "waiting" | "fetched" | "none" | "failed";

/** CRM-4.0: kundemøte (tabellen crm_meetings; public.meetings er prosjektmøter fra før). */
export interface DBCrmMeeting {
  id: string;
  opportunity_id: string | null;
  customer_id: string | null;
  lead_id: string | null;
  host_user_id: string;
  booked_by: string;
  contact_id: string | null;
  title: string;
  message: string | null;
  location_type: MeetingLocationType;
  address: string | null;
  start_at: string;
  end_at: string;
  google_event_id: string | null;
  google_calendar_id: string | null;
  html_link: string | null;
  meet_code: string | null;
  meet_uri: string | null;
  meet_space_name: string | null;
  status: MeetingStatus;
  attended_customer: boolean | null;
  transcription_status: TranscriptionStatus;
  activity_id: string | null;
  notes: string | null;
  details: Record<string, unknown>;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export type TranscriptSide = "solvrev" | "customer";

export interface TranscriptParticipant {
  name: string;
  side: TranscriptSide;
  user_id?: string;
}

export interface TranscriptEntry {
  participant: string;
  text: string;
  start_offset_ms: number;
  end_offset_ms: number;
}

/** CRM-4.0: rå transkripsjon fra Meet. Slettes etter crm_transcript_retention_days. */
export interface DBMeetingTranscript {
  id: string;
  meeting_id: string;
  conference_record: string | null;
  transcript_name: string | null;
  participants: TranscriptParticipant[];
  entries: TranscriptEntry[];
  entry_count: number;
  duration_seconds: number | null;
  fetched_at: string;
  purge_after: string;
}

export type MeetingSummaryStatus = "draft" | "approved" | "discarded";

export interface MeetingDecision {
  text: string;
  offset_ms: number | null;
}

export interface MeetingActionItem {
  id: string;
  description: string;
  side: TranscriptSide;
  owner_name: string | null;
  owner_user_id: string | null;
  due_date: string | null;
  offset_ms: number | null;
  /** Skrives tilbake av approve_meeting_summary når punktet er lagt i oppfølging. */
  follow_up_id?: string | null;
  /** Klientens valg ved godkjenning. */
  add_follow_up?: boolean;
}

/** CRM-4.0: møtereferat (utkast fra Claude, godkjent av verten). */
export interface DBMeetingSummary {
  id: string;
  meeting_id: string;
  source: "transcript" | "notes";
  status: MeetingSummaryStatus;
  summary: string;
  decisions: MeetingDecision[];
  action_items: MeetingActionItem[];
  customer_needs: string[];
  next_step_suggestion: string | null;
  discard_reason: string | null;
  model: string | null;
  usage: Record<string, unknown> | null;
  approved_by: string | null;
  approved_at: string | null;
  activity_id: string | null;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// VIB-2925: frilanserbanken
// ---------------------------------------------------------------------------

export type FreelancerKind = "person" | "company";

export interface FreelancerLink {
  label: string;
  url: string;
}

export interface DBFreelancerDiscipline {
  id: string;
  label: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
}

export interface DBFreelancer {
  id: string;
  name: string;
  kind: FreelancerKind;
  company_name: string | null;
  organization_number: string | null;
  email: string | null;
  phone: string | null;
  location: string | null;
  day_rate_nok: number | null;
  hour_rate_nok: number | null;
  rate_note: string | null;
  rate_updated_at: string | null;
  links: FreelancerLink[];
  tags: string[];
  notes: string | null;
  monday_item_ids: string[];
  archived_at: string | null;
  archived_by: string | null;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

/** VIB-2927: frilanser på ekstern budsjettlinje. Skriver aldri til linjen. */
export interface DBBudgetLineFreelancer {
  budget_line_id: string;
  freelancer_id: string;
  project_id: string;
  created_by: string | null;
  created_at: string;
}

/** VIB-2927: PowerOffice-leverandør koblet til frilanser, eller merket «Ikke frilanser». */
export interface DBFreelancerSupplier {
  id: string;
  po_supplier_id: number | null;
  supplier_key: string | null;
  supplier_name: string;
  freelancer_id: string | null;
  is_not_freelancer: boolean;
  created_by: string | null;
  created_at: string;
}

/** Rad fra RPC-en freelancer_project_history. */
export interface FreelancerProjectHistoryRow {
  freelancer_id: string;
  freelancer_name: string;
  project_id: string;
  project_code: string | null;
  project_name: string;
  /** VIB-2934: kunde og PL på prosjektet. */
  customer_name: string | null;
  production_lead_name: string | null;
  /** «valgt» = frilanseren er satt på linjen i Timetildeling, «bilag» = bilag fra koblet leverandør er fordelt på linjen. */
  lines: { budget_line_id: string; description: string | null; estimated_cost: number | null; source: "valgt" | "bilag" }[];
  invoiced: number;
  voucher_count: number;
  last_voucher_date: string | null;
  last_used: string | null;
  source: "line" | "voucher" | "both";
}

export type FreelanceSupplierStatus = "unlinked" | "linked" | "not_freelancer";

/** VIB-2934: rolle for en kontaktperson i frilanserbanken. */
export type FreelancerContactRole = "agent" | "producer" | "contact" | "billing" | "other";

/** VIB-2934: kontaktperson for en frilanser eller et firma. */
export interface DBFreelancerContact {
  id: string;
  freelancer_id: string;
  name: string;
  role: FreelancerContactRole;
  /** Fritekst når rollen er «Annet». */
  role_note: string | null;
  email: string | null;
  /** E.164 når det kunne tolkes, ellers som skrevet. */
  phone: string | null;
  sort_order: number;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

/** Rad fra RPC-en freelance_supplier_overview. */
export interface FreelanceSupplierOverviewRow {
  po_supplier_id: number | null;
  supplier_key: string | null;
  supplier_name: string;
  voucher_count: number;
  project_count: number;
  invoiced: number;
  last_voucher_date: string | null;
  line_descriptions: string[];
  link_id: string | null;
  status: FreelanceSupplierStatus;
  freelancer_id: string | null;
  freelancer_name: string | null;
  suggested_freelancer_id: string | null;
  suggested_freelancer_name: string | null;
}

export interface DBFreelancerDisciplineLink {
  freelancer_id: string;
  discipline_id: string;
  is_primary: boolean;
}

export interface DBFreelancerReview {
  id: string;
  freelancer_id: string;
  rating: number;
  comment: string | null;
  project_id: string | null;
  source: "manual" | "monday_import";
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface DBFreelancerComment {
  id: string;
  freelancer_id: string;
  parent_comment_id: string | null;
  author_id: string | null;
  body: string;
  body_html: string | null;
  mentioned_user_ids: string[];
  monday_update_id: string | null;
  monday_author_name: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface DBFreelancerAttachment {
  id: string;
  comment_id: string;
  freelancer_id: string;
  storage_path: string | null;
  name: string;
  file_extension: string | null;
  file_size: number | null;
  monday_asset_id: string | null;
  uploaded_by: string | null;
  created_at: string;
}

/** VIB-2921: servicesak fra viewet equipment_service_ticket_list. cost er NULL for andre enn admin. */
export interface DBEquipmentServiceTicket {
  id: string;
  item_id: string;
  item_name: string;
  item_status: DBEquipmentItem["status"];
  item_department_id: string | null;
  item_department_name: string | null;
  description: string;
  usable: boolean;
  status: "open" | "at_vendor" | "resolved";
  vendor: string | null;
  cost: number | null;
  image_path: string | null;
  resolved_at: string | null;
  reported_by: string;
  reported_by_name: string | null;
  created_at: string;
  updated_at: string;
}

/** VIB-2938: lokasjonstype (admin-styrt liste). */
export interface DBLocationType {
  id: string;
  label: string;
  indoor: "indoor" | "outdoor" | "both";
  sort_order: number;
  is_active: boolean;
}

export interface DBLocationLink {
  label: string;
  url: string;
}

/** VIB-2940: Forslag, Befart, Valgt, Brukt, Forkastet. */
export type ProjectLocationStatus = "suggested" | "scouted" | "selected" | "used" | "rejected";

export interface DBProjectLocation {
  id: string;
  project_id: string;
  location_id: string;
  status: ProjectLocationStatus;
  start_date: string | null;
  end_date: string | null;
  note: string | null;
  legacy_text: string | null;
  source: "manual" | "import";
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

/** VIB-2938: lokasjon. Posisjonen kommer aldri fra Google (se position_source). */
export interface DBLocation {
  id: string;
  slug: string;
  name: string;
  type_id: string | null;
  address: string | null;
  postal_code: string | null;
  postal_place: string | null;
  municipality_number: string | null;
  municipality_name: string | null;
  district: string | null;
  country_code: string;
  lat: number | null;
  lon: number | null;
  position_source: "address" | "pin" | "photo" | "import" | null;
  google_place_id: string | null;
  tags: string[];
  description: string | null;
  parking: string | null;
  power: string | null;
  light: string | null;
  access: string | null;
  rental_price: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  links: DBLocationLink[];
  cover_image_id: string | null;
  legacy_id: string | null;
  legacy_history: string | null;
  legacy_created_by: string | null;
  archived_at: string | null;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

/** VIB-2938: rad fra locations_overview_v (lista). */
export interface DBLocationOverview {
  id: string;
  slug: string;
  name: string;
  type_id: string | null;
  type_label: string | null;
  indoor: DBLocationType["indoor"] | null;
  address: string | null;
  postal_code: string | null;
  postal_place: string | null;
  municipality_number: string | null;
  municipality_name: string | null;
  district: string | null;
  country_code: string;
  lat: number | null;
  lon: number | null;
  tags: string[];
  description: string | null;
  archived_at: string | null;
  legacy_id: string | null;
  created_at: string;
  updated_at: string;
  image_count: number;
  cover_path: string | null;
  /** Koblinger til prosjekter som ikke er slettet (VIB-2940). */
  project_count: number;
  /** Av dem med status Brukt. */
  used_count: number;
}

/** VIB-2938: bilde på en lokasjon (bøtta location-media). */
export interface DBLocationImage {
  id: string;
  location_id: string;
  storage_path: string;
  thumb_path: string | null;
  sort_order: number;
  width: number | null;
  height: number | null;
  bytes: number | null;
  taken_at: string | null;
  lat: number | null;
  lon: number | null;
  source: string | null;
  legacy_key: string | null;
  uploaded_by: string | null;
  created_at: string;
}

/** VIB-2991: vurdering av en fast sjekk i «Dette bør du se på» på Måltall. */
export interface DBMaaltallCheckFeedback {
  id: string;
  user_id: string;
  check_type: string;
  check_key: string;
  rating: "up" | "down";
  created_at: string;
  updated_at: string;
}
